import React, { FC, useMemo } from 'react';
import { Button, Tooltip } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import { dateFormatter, DATE_ONLY_FORMAT } from '../../utils/DataFormatterUtils';
import {
	formatLeakRatePercent,
	formatPounds,
	getApplianceCategoryLabel,
	getThresholdPercentForCategory,
} from '../../utils/RefrigerantUtils';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import RefrigerantBandDisplay from './RefrigerantBandDisplay';
import RefrigerantLeakRateBar from './RefrigerantLeakRateBar';
import './RefrigerantAssetRowDisplay.less';

interface RefrigerantAssetRowDisplayProps {
	/** An OpenSearch asset document — the refrigerant scalars only exist on that shape. */
	asset: any;
	/** The buyer's `refrigerantConfig.thresholdOverrides`, so the row shows the EFFECTIVE threshold. */
	thresholdOverrides?: any;
	onLogEvent?: (asset: any) => void;
	onRaiseLeakRepair?: (asset: any) => void;
	canLogEvent?: boolean;
	canRaiseWorkOrder?: boolean;
}

const MetaItem = ({ icon, children }) =>
	children ? (
		<span className="footnoteregular flex flex-row items-center gap-2xs text-character-secondary">
			{icon}
			{children}
		</span>
	) : null;

/**
 * One appliance on the Refrigerant Tracking ▸ Assets list.
 *
 * Everything regulated on this row — the rate, the band, the pounds — is read straight off the
 * OpenSearch projection, which the server wrote from the append-only ledger. The ONE number derived
 * here is the threshold, and only because the projection does not carry it: the appliance's category
 * plus the buyer's overrides is exactly what the server banded against, so the two cannot disagree.
 * `RefrigerantAssetSummary.thresholdPercent` is authoritative wherever it is available (the detail
 * tab); this is the index screen's stand-in for it.
 */
export const RefrigerantAssetRowDisplay: FC<RefrigerantAssetRowDisplayProps> = ({
	asset,
	thresholdOverrides,
	onLogEvent,
	onRaiseLeakRepair,
	canLogEvent = true,
	canRaiseWorkOrder = true,
}) => {
	const applianceCategory = nullSafeGet('applianceCategory', asset);
	const leakRatePercent = nullSafeGetWithZero('refrigerantLeakRatePercent', asset);
	const band = nullSafeGet('refrigerantComplianceBand', asset);
	const nextInspectionAt = nullSafeGet('refrigerantNextInspectionAt', asset);
	// The statutory repair deadline, projected onto the asset document beside the inspection date. A
	// non-null value IS an open repair obligation — the server clears it once a verified repair
	// discharges the exceedance — so its presence is the whole test, exactly as for `verificationDueAt`.
	const repairDueAt = nullSafeGet('refrigerantRepairDueAt', asset);
	const verificationDueAt = nullSafeGet('refrigerantVerificationDueAt', asset);
	const isActive = nullSafeGetOrElse('isActive', asset, true);

	const thresholdPercent = useMemo(
		() => getThresholdPercentForCategory(applianceCategory, thresholdOverrides),
		[applianceCategory, thresholdOverrides]
	);

	const rateLine =
		thresholdPercent === null
			? formatLeakRatePercent(leakRatePercent)
			: `${formatLeakRatePercent(leakRatePercent)} / ${thresholdPercent}%`;

	const stopAnd = (handler) => (event) => {
		event.stopPropagation();
		handler && handler(asset);
	};

	return (
		<div className="flex w-full flex-row flex-wrap items-center justify-between gap-4">
			<div className="flex flex-col gap-2" style={{ minWidth: 260, flex: '2 1 320px' }}>
				<div className="flex flex-row flex-wrap items-baseline gap-2">
					<span className="h5medium text-character-primary">{nullSafeGet('name', asset)}</span>
					{applianceCategory ? (
						<span className="footnoteregular text-character-secondary">
							{getApplianceCategoryLabel(applianceCategory)}
						</span>
					) : null}
					{!isActive ? (
						// Geometry in Tailwind; the two colours stay inline because neither has an exact token —
						// `character-primary`/`-secondary` are 0.85 and 0.45 alpha, and the closest greys to
						// #f2f2f2 are grey-3 (#f5f5f5) and grey-4 (#f0f0f0). Substituting would silently shift a
						// design-reviewed pill rather than remove a magic value.
						<span
							className="footnoteregular rounded-full px-2 py-0.5"
							style={{ color: 'rgba(0,0,0,0.65)', background: '#f2f2f2' }}
						>
							Inactive
						</span>
					) : null}
				</div>
				<div className="flex flex-row flex-wrap items-center gap-3">
					<MetaItem icon={<EnvironmentOutlined />}>
						{nullSafeGet('locationName', asset) || nullSafeGet('location.name', asset)}
					</MetaItem>
					<MetaItem icon={null}>{nullSafeGet('assetNumber', asset)}</MetaItem>
					<MetaItem icon={null}>{nullSafeGet('refrigerantTypeCode', asset)}</MetaItem>
					<MetaItem icon={null}>
						{nullSafeGet('assetTypeName', asset) || nullSafeGet('assetType.name', asset)}
					</MetaItem>
				</div>
				<div className="flex flex-row flex-wrap items-center gap-3">
					{/* First in the row, ahead of the inspection date: a repair deadline is a statutory clock
					    with a consequence attached, an inspection date is a schedule. Coloured like the
					    verification notice rather than secondary-grey, because an open repair obligation is
					    the same class of fact — something this appliance owes — and the two read as a pair. */}
					{repairDueAt ? (
						<Tooltip title="This appliance is over its leak-rate threshold and owes a repair by this date.">
							<span className="footnoteregular" style={{ color: 'rgb(213, 125, 5)' }}>
								Leak Repair Due {dateFormatter(repairDueAt, DATE_ONLY_FORMAT)}
							</span>
						</Tooltip>
					) : null}
					{nextInspectionAt ? (
						<span className="footnoteregular text-character-secondary">
							Next Inspection: {dateFormatter(nextInspectionAt, DATE_ONLY_FORMAT)}
						</span>
					) : null}
					{/* A non-null verification due date IS the open obligation — the server clears the field
					    once a verification test satisfies it — so its mere presence is what to surface. */}
					{verificationDueAt ? (
						<Tooltip title="A leak repair on this appliance is awaiting a verification test.">
							<span className="footnoteregular" style={{ color: 'rgb(213, 125, 5)' }}>
								Verification test due {dateFormatter(verificationDueAt, DATE_ONLY_FORMAT)}
							</span>
						</Tooltip>
					) : null}
				</div>
			</div>

			{/* Compliance figures and the row's actions share ONE grid cell, the way the asset row shares
			    one with its asset-number/QR block: the actions replace the figures on hover instead of
			    reserving a column of their own, so the resting row spends its width on data and nothing
			    reflows as the pointer crosses it. */}
			<div
				className="refrigerant-asset-row__swap grid"
				style={{ minWidth: 240, flex: '2 1 260px' }}
			>
				<div className="refrigerant-asset-row__metrics col-start-1 row-start-1 flex flex-col gap-2">
					<div className="flex flex-row items-center gap-2">
						<RefrigerantBandDisplay band={band} />
						<span className="bodymedium text-character-primary">{rateLine}</span>
					</div>
					<RefrigerantLeakRateBar
						leakRatePercent={leakRatePercent}
						thresholdPercent={thresholdPercent}
						band={band}
					/>
					<div className="flex flex-row flex-wrap justify-between gap-2">
						<span className="footnoteregular text-character-secondary">
							{formatPounds(nullSafeGetWithZero('refrigerantAddedLbTrailing12Mo', asset))} charged
							(trailing 12 mo)
						</span>
						<span className="footnoteregular text-character-secondary">
							{formatPounds(nullSafeGetWithZero('fullChargeLb', asset))} full charge
						</span>
					</div>
				</div>

				<div className="refrigerant-asset-row__actions col-start-1 row-start-1 flex flex-row items-center gap-5 justify-self-end">
					{/* Ghost + primary, the asset row's pairing: these two land on the same screens as Edit /
					    Request Service, and a row action styled its own way reads as a different kind of
					    control than it is. OWButtonWithSupplierManagedLock, not Button, because the header's
					    Log Event beside this one is locked for a buyer whose company is supplier-managed, and
					    a per-row action that stayed a plain Button was a way around that lock. */}
					{canLogEvent ? (
						<OWButtonWithSupplierManagedLock type="ghost" onClick={stopAnd(onLogEvent)}>
							Log Event
						</OWButtonWithSupplierManagedLock>
					) : null}
					{canRaiseWorkOrder ? (
						<Button type="primary" onClick={stopAnd(onRaiseLeakRepair)}>
							Request Service
						</Button>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default RefrigerantAssetRowDisplay;
